import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Calendar, Home, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
type UserRole = 'renter' | 'landlord';
interface Step {
  number: number;
  title: string;
  description: string;
  benefits: string[];
}
const roleSteps: Record<UserRole, Step[]> = {
  renter: [{
    number: 1,
    title: 'Apply Online',
    description: 'Complete your profile in 10 minutes. We verify your income and identity.',
    benefits: ['No bulk payment stress', 'Build credit history']
  }, {
    number: 2,
    title: 'Get Approved',
    description: 'Our algorithm assesses your affordability. Most applications approved in 24 hours.',
    benefits: ['Fast approval process', 'Transparent criteria']
  }, {
    number: 3,
    title: 'Move In & Pay Monthly',
    description: 'Landlord gets paid upfront. You pay comfortable monthly installments.',
    benefits: ['Flexible payments', 'Auto-debit convenience']
  }],
  landlord: [{
    number: 1,
    title: 'List Property',
    description: 'Add your property details and set your annual rent amount.',
    benefits: ['Free listing', 'Verified tenants only']
  }, {
    number: 2,
    title: 'Get Matched',
    description: 'We match you with pre-approved tenants backed by Renta.',
    benefits: ['100% payment guarantee', 'Zero default risk']
  }, {
    number: 3,
    title: 'Receive Payment',
    description: 'Get your full annual rent upfront. We handle monthly collections.',
    benefits: ['Upfront payment', 'Hassle-free management']
  }]
};
const roleConfig: Record<UserRole, {
  label: string;
  icon: React.ElementType;
  gradient: string;
}> = {
  renter: {
    label: 'For Renters',
    icon: Calendar,
    gradient: 'from-blue-600 to-blue-700'
  },
  landlord: {
    label: 'For Landlords',
    icon: Home,
    gradient: 'from-green-600 to-emerald-600'
  }
};
export function HowItWorks() {
  const [activeRole, setActiveRole] = useState<UserRole>('renter');
  const steps = roleSteps[activeRole];
  const config = roleConfig[activeRole];
  return <section id="how-it-works" className="py-20 lg:py-32 bg-gray-50" data-id="element-1247">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1248">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-1249">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-id="element-1250">
            How Renta Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1251">
            Choose your role to see how Renta transforms your experience
          </p>
        </div>

        {/* Role Tabs */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 mb-16" data-id="element-1252">
          {(Object.keys(roleConfig) as UserRole[]).map(role => {
          const {
            label,
            icon: Icon
          } = roleConfig[role];
          const isActive = activeRole === role;
          return <motion.button key={role} onClick={() => setActiveRole(role)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className={`
                  relative px-6 py-3.5 rounded-xl font-semibold text-base
                  transition-all duration-300 flex items-center justify-center gap-2
                  ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}
                `} data-id="element-1253">
                <Icon className="h-5 w-5" data-id="element-1254" />
                {label}
                {isActive && <motion.div layoutId="activeTab" className="absolute inset-0 bg-blue-600 rounded-xl -z-10" transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1255" />}
              </motion.button>;
        })}
        </div>

        {/* Steps */}
        <AnimatePresence mode="wait" data-id="element-1256">
          <motion.div key={activeRole} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1257">
            {steps.map((step, index) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-1258">
                {/* Step Number */}
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white font-bold text-lg mb-6" data-id="element-1259">
                  {step.number}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1260">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-1261">
                  {step.description}
                </p>

                {/* Benefits */}
                <div className="space-y-2" data-id="element-1262">
                  {step.benefits.map((benefit, i) => <div key={i} className="flex items-start gap-2" data-id="element-1263">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1264" />
                      <span className="text-sm text-gray-700" data-id="element-1265">{benefit}</span>
                    </div>)}
                </div>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="text-center" data-id="element-1266">
          <Link to="/signup" className={`
              inline-flex items-center justify-center px-8 py-4 
              rounded-full font-bold text-white text-lg
              bg-gradient-to-r ${config.gradient}
              shadow-xl hover:shadow-2xl
              transform hover:scale-105 transition-all duration-300
            `} data-id="element-1267">
            Get Started as {roleConfig[activeRole].label.replace('For ', '')}
            <ArrowRight className="ml-2 h-5 w-5" data-id="element-1268" />
          </Link>
        </motion.div>
      </div>
    </section>;
}